import {enableProdMode, ErrorHandler, APP_INITIALIZER, importProvidersFrom} from '@angular/core';
import {polyfill} from 'mobile-drag-drop';
import {scrollBehaviourDragImageTranslateOverride} from 'mobile-drag-drop/scroll-behaviour';
import {APP_CONFIG, AppConfig} from './app.config';

import {environment} from '@environments/environment';
import * as Sentry from "@sentry/angular";
import {AppComponent} from './app/app.component';
import {MatDialogModule} from '@angular/material/dialog';
import {
  PrescriptionDetailEffects
} from '@app/areas/prescriptions/components/prescription-detail/state/prescription-detail.effects';
import {EffectsModule} from '@ngrx/effects';
import {
  prescriptionDetailReducer
} from '@app/areas/prescriptions/components/prescription-detail/state/prescription-detail.reducer';
import {StoreModule} from '@ngrx/store';
import {ClinicianDashboardComponentsModule} from '@clinician/shared/components/clinician-dashboard-components.module';
import {AccountModule} from '@app/account/account.module';
import {SharedModule} from '@app/shared/shared.module';
import {AppRoutingModule} from './app/app-routing.module';
import {provideAnimations} from '@angular/platform-browser/animations';
import {BrowserModule, bootstrapApplication} from '@angular/platform-browser';
import {Router} from '@angular/router';
import {TIME_ZONE} from '@shared/injectors/time-zone.service';
import {CacheInterceptorInterceptor} from '@shared/interceptors/cache-interceptor.interceptor';
import {CustomInterceptor} from '@app/shared/interceptors/httpInterceptor';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import { configureDebugConsoleLogging } from '@app/shared/common/debug-console';
import {TitleStrategy} from '@angular/router';
import {FitMedTitleStrategy} from '@app/shared/services/core/title/fitmed-title.strategy';

configureDebugConsoleLogging();

const pad = (num, length) => {
  let str = '' + num;
  while (str.length < length) {
    str = '0' + str;
  }
  return str;
};

const offset = (new Date()).getTimezoneOffset();
const timeZone = ((offset < 0 ? '+' : '-') +
  // eslint-disable-next-line radix
  pad(parseInt(String(Math.abs(offset / 60))), 2) +
  pad(Math.abs(offset % 60), 2));

fetch('/assets/configurations/config.json')
  .then((response) => response.json())
  .then((config) => {
    if (environment.production) {
      enableProdMode();
    }

    const newConf = Object.assign(new AppConfig(), config as AppConfig);

    const enableSentry = newConf.environment === 'production';

    if (enableSentry) {
      Sentry.init({
        dsn: "https://8e8bee0f74f93889b53a727181223b10@o4508576931971072.ingest.de.sentry.io/4508577082048592",
        integrations: [
          Sentry.browserTracingIntegration() as any,
        ],
        tracesSampleRate: 0.2,
        environment: newConf.environment
      });
    }

    // overwriting abp values
    environment.apis.default.url = newConf.base;
    // environment.oAuthConfig.issuer = 'https://api.fitmed.app/';
    // environment.oAuthConfig.redirectUri = newConf.base;

    bootstrapApplication(AppComponent, {
      providers: [
        {provide: APP_CONFIG, useValue: newConf},
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(
          BrowserModule,
          AppRoutingModule,
          // CoreModule.forRoot({
          //   environment,
          //   registerLocaleFn: registerLocale
          // }),
          SharedModule.forRoot(),
          AccountModule,
          ClinicianDashboardComponentsModule,
          // OAuthModule.forRoot(),
          StoreModule.forRoot({
            prescriptionDetail: prescriptionDetailReducer,
          }),
          EffectsModule.forRoot([
            PrescriptionDetailEffects,
          ]),
          MatDialogModule
        ),
        {provide: HTTP_INTERCEPTORS, useClass: CustomInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: CacheInterceptorInterceptor, multi: true},
        {provide: TIME_ZONE, useValue: timeZone},
        {provide: TitleStrategy, useClass: FitMedTitleStrategy},
        ...(enableSentry ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {
            },
            deps: [Sentry.TraceService],
            multi: true,
          },
        ] : []),
        provideAnimations()
      ]
    })
      .catch((err) => console.error(err));
  });

/**
 * Drag & drop compatibility in mobile browsers polyfill. More information on drag+drop support for mobile can be found:
 * https://www.npmjs.com/package/ngx-drag-drop#touch-support
 */
polyfill({
  // use this to make use of the scroll behaviour
  dragImageTranslateOverride: scrollBehaviourDragImageTranslateOverride
});

// workaround to make scroll prevent work in iOS Safari > 10
try {
  window.addEventListener('touchmove', function () {
  }, {passive: false});
} catch (e) {  // this error will be thrown if not found to be a touch device
}

/**
 * Polyfill requires dragenter-listener to be disabled for compatibility of the dropzone firing to work. More
 * information in the docs for the mobile-drag-drop library can be found:
 * https://www.npmjs.com/package/mobile-drag-drop#polyfill-requires-dragenter-listener
 *
 * Information on the dragenter event:
 * https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dragenter_event
 */
addEventListener('dragenter', (event) => event.preventDefault());
